import { PapiDeviation } from '@wix/da-papi-types';
import { AttachmentType, FileAttachment, MessageAttachment, MessageItem } from '../types';

import { DeviationTypes } from '@wix/da-shared-react/pkg/types/deviation';
import DeviationMediaService from '@wix/da-shared-react/pkg/utils/DeviationMediaService';

export function factoryAttachmentFromDeviationAndType(
  deviation: PapiDeviation
): MessageAttachment | undefined {
  const type = DeviationMediaService.getDeviationType(deviation);
  const nonImagePartial = {
    attachmentType: AttachmentType.NonImageDeviation,
    link: deviation.url ?? '',
    title: deviation.title,
    isMature: deviation.isMature,
  };
  switch (type) {
    case DeviationTypes.UNKNOWN:
      return {
        attachmentType: AttachmentType.File,
        link: deviation.url ?? '',
        type: deviation.filetype ?? '',
        title: deviation.title,
        isMature: deviation.isMature,
      };
    case DeviationTypes.TIER:
      const tierImg = DeviationMediaService.getSmallestThumbThatFullyCovers(
        deviation.coverImage ?? deviation,
        400,
        266,
        thumbs => thumbs[thumbs.length - 1]
      );
      return {
        attachmentType: AttachmentType.Money,
        link: deviation.url ?? '',
        title: deviation.title,
        isMature: deviation.isMature || deviation.coverImage?.isMature,
        image: tierImg?.src ?? '',
        width: tierImg?.width ?? 0,
        height: tierImg?.height ?? 0,
        nonImageType: 'tier',
      };
    case DeviationTypes.COMMISSION:
      const commissionImg = DeviationMediaService.getSmallestThumbThatFullyCovers(
        deviation.coverImage ?? deviation,
        400,
        266,
        thumbs => thumbs[thumbs.length - 1]
      );
      return {
        attachmentType: AttachmentType.Money,
        link: deviation.url ?? '',
        title: deviation.title,
        isMature: deviation.isMature || deviation.coverImage?.isMature,
        image: commissionImg?.src ?? '',
        width: commissionImg?.width ?? 0,
        height: commissionImg?.height ?? 0,
        nonImageType: 'commission',
      };
    case DeviationTypes.ORIGINALS:
      const originalsImg = DeviationMediaService.getSmallestThumbThatFullyCovers(
        deviation.coverImage ?? deviation,
        400,
        266,
        thumbs => thumbs[thumbs.length - 1]
      );
      return {
        attachmentType: AttachmentType.Money,
        link: deviation.url ?? '',
        title: deviation.title,
        isMature: deviation.isMature || deviation.coverImage?.isMature,
        image: originalsImg?.src ?? '',
        width: originalsImg?.width ?? 0,
        height: originalsImg?.height ?? 0,
        nonImageType: 'original',
      };
    case DeviationTypes.JOURNAL:
      return { ...nonImagePartial, nonImageType: 'journal' };
    case DeviationTypes.LITERATURE:
      return { ...nonImagePartial, nonImageType: 'literature' };

    case DeviationTypes.PDF:
      return { ...nonImagePartial, nonImageType: 'file' };
    case DeviationTypes.STATUS:
      return { ...nonImagePartial, nonImageType: 'status' };
    case DeviationTypes.POLL:
      return { ...nonImagePartial, nonImageType: 'poll' };
    case DeviationTypes.IMAGE:
    case DeviationTypes.FILM:
      if (deviation.isDefaultImage) {
        return {
          attachmentType: AttachmentType.File,
          link: deviation.url,
          type: deviation.filetype,
          title: deviation.title,
          isMature: deviation.isMature,
        } as FileAttachment;
      }
      const media = DeviationMediaService.getSmallestThumbThatFullyCovers(
        deviation,
        400,
        266,
        thumbs => thumbs[thumbs.length - 1]
      );
      return {
        attachmentType: AttachmentType.ImageDeviation,
        image: media?.src ?? '',
        width: media?.width ?? 0,
        height: media?.height ?? 0,
        link: deviation.url ?? '',
        isMature: deviation.isMature,
      };
    default:
      return undefined;
  }
}

export function getAttachmentsFromMessageItem(message: MessageItem): MessageAttachment[] {
  return ['a', 'b', 'c', 'd', 'e', 'f'].reduce((accumulator, slot) => {
    const attachment = message.msg[`${slot}_attachment`];
    return attachment ? [...accumulator, attachment] : accumulator;
  }, []);
}

export function attachmentArrayToFormData(attachments: MessageAttachment[]) {
  return attachments.length > 0
    ? attachments.reduce((acc, attachment) => {
        for (const slot of ['a', 'b', 'c', 'd', 'e', 'f']) {
          if (!acc[slot]) {
            acc[slot] = attachment;
            return acc;
          }
        }
        return acc;
      }, {})
    : undefined;
}

/**
 * Filters out upload progress attachments from the attachments array.
 * Upload progress attachments should never be sent with messages.
 */
export function filterOutUploadProgressAttachments(
  attachments: MessageAttachment[]
): MessageAttachment[] {
  return attachments.filter(
    attachment => attachment.attachmentType !== AttachmentType.UploadProgress
  );
}
